import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Combobox } from "@dashboard/components/Combobox";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Checkbox, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import {
  getWsmDealerErrorMessage,
  getWsmDealerFieldError,
  getWsmDealerFormErrors,
} from "../../errors";
import { customerMessages, railMessages } from "../../messages";
import { WsmDealerCustomerFormData } from "../../types";
import { wsmDealerCustomerListPath, wsmDealerGroupUrl } from "../../urls";
import WsmDealerHelpCard from "../WsmDealerHelpCard/WsmDealerHelpCard";

export interface WsmDealerCustomerDetailsPageProps {
  title: string;
  initialData: WsmDealerCustomerFormData;
  /** Assign only: the customer picker. A saved row is keyed by its user. */
  customerOptions?: Option[];
  onCustomerSearch?: (query: string) => void;
  customerSearchLoading?: boolean;
  groupOptions: Option[];
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmDealerCustomerFormData) => void;
  onUnassign?: () => void;
}

export const WsmDealerCustomerDetailsPage = ({
  title,
  initialData,
  customerOptions,
  onCustomerSearch,
  customerSearchLoading,
  groupOptions,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onUnassign,
}: WsmDealerCustomerDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmDealerCustomerFormData>(initialData);
  const change = (patch: Partial<WsmDealerCustomerFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const userError = getWsmDealerFieldError(errors, "user");
  const groupError = getWsmDealerFieldError(errors, "group");
  const formErrors = getWsmDealerFormErrors(errors, intl);
  const isAssign = !!onCustomerSearch;
  const currentGroup = groupOptions.find(option => option.value === data.groupId);

  return (
    <DetailPageLayout>
      <TopNav href={wsmDealerCustomerListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-dealer-customer-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(customerMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text
                key={message}
                size={3}
                color="critical1"
                data-test-id="wsm-dealer-customer-error"
              >
                {message}
              </Text>
            ))}
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              {isAssign ? (
                <Combobox
                  data-test-id="wsm-dealer-customer-field"
                  name="customer"
                  label={intl.formatMessage(customerMessages.customerField)}
                  disabled={disabled}
                  options={customerOptions ?? []}
                  loading={customerSearchLoading}
                  fetchOptions={onCustomerSearch}
                  value={{ label: data.userLabel, value: data.userId }}
                  error={!!userError}
                  helperText={
                    getWsmDealerErrorMessage(userError, intl) ??
                    intl.formatMessage(customerMessages.customerFieldHelp)
                  }
                  onChange={event =>
                    change({
                      userId: event.target.value,
                      userLabel:
                        (customerOptions ?? []).find(option => option.value === event.target.value)
                          ?.label ?? event.target.value,
                    })
                  }
                />
              ) : (
                <Input
                  data-test-id="wsm-dealer-customer-field"
                  label={intl.formatMessage(customerMessages.customerField)}
                  disabled
                  value={data.userLabel}
                  helperText={intl.formatMessage(customerMessages.customerFieldHelp)}
                />
              )}
              <Select
                data-test-id="wsm-dealer-customer-group"
                name="group"
                label={intl.formatMessage(customerMessages.groupField)}
                disabled={disabled}
                options={groupOptions}
                value={data.groupId}
                error={!!groupError}
                helperText={
                  getWsmDealerErrorMessage(groupError, intl) ??
                  intl.formatMessage(customerMessages.groupFieldHelp)
                }
                onChange={value => change({ groupId: String(value) })}
              />
            </Box>
            <FormSpacer />
            <Checkbox
              data-test-id="wsm-dealer-customer-tax-exempt"
              disabled={disabled}
              name="taxExempt"
              checked={data.taxExempt}
              onCheckedChange={checked => change({ taxExempt: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(customerMessages.taxExempt)}</Text>
            </Checkbox>
            <Box paddingLeft={5} paddingTop={1}>
              <Text size={2} color="default2">
                {intl.formatMessage(customerMessages.taxExemptHelp)}
              </Text>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        {currentGroup ? (
          <DashboardCard data-test-id="wsm-dealer-customer-rail">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(railMessages.customerTitle)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="grid" gap={3}>
                <Text size={3}>{currentGroup.label}</Text>
                {/* A grid child stretches; the pill has to hug its own label. */}
                <Box display="flex">
                  <Pill
                    color={data.taxExempt ? "success" : "generic"}
                    label={`${intl.formatMessage(customerMessages.taxExempt)}: ${intl.formatMessage(
                      data.taxExempt
                        ? customerMessages.taxExemptYes
                        : customerMessages.taxExemptNo,
                    )}`}
                    data-test-id="wsm-dealer-customer-rail-tax"
                  />
                </Box>
                <Text
                  size={3}
                  color="default2"
                  __cursor="pointer"
                  __textDecoration="underline"
                  onClick={() => navigate(wsmDealerGroupUrl(String(currentGroup.value)))}
                  data-test-id="wsm-dealer-customer-rail-group-link"
                >
                  {intl.formatMessage(railMessages.openGroup)}
                </Text>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        ) : (
          <WsmDealerHelpCard />
        )}
      </DetailPageLayout.RightSidebar>

      <Savebar>
        {onUnassign && <Savebar.DeleteButton onClick={onUnassign} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmDealerCustomerListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled || !data.userId || !data.groupId}
          data-test-id="wsm-dealer-customer-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmDealerCustomerDetailsPage;
